import { writable, derived } from 'svelte/store';

// Selected cryptocurrency
export const selectedSymbol = writable('BTC/USD');

// All available symbols
export const symbols = writable([]);

// Portfolio positions
export const positions = writable([]);

// Current quote for selected symbol
export const currentQuote = writable(null);

// OHLCV data for charts
export const ohlcvData = writable([]);

// Order book data
export const orderBook = writable({ bids: [], asks: [] });

// Order history
export const orders = writable([]);

// UI state
export const loading = writable(false);
export const error = writable(null);

// Trading panel state
export const tradeType = writable('buy'); // 'buy' or 'sell'
export const orderType = writable(1); // 1=Market, 2=Limit, etc.
export const tradeAmount = writable('');
export const tradePrice = writable('');

// Backtesting state — write primitives; backtestStatus is DERIVED from them (not a fifth synchronized
// writable), matching the write-primitives / derive-composites pattern below.
export const backtestResult = writable(null);   // the latest BacktestResult (live run or loaded history)
export const backtestRunning = writable(false);  // true while a run's blocking POST is in flight
export const backtestError = writable(null);     // run-level error message (cleared on a new run)
export const selectedRunId = writable(null);     // the history row currently shown, if any
// TASK-20260728: the BacktestRunSummary being loaded (set synchronously on a history click so the results
// header + skeletons render instantly from the row's stored fields while the heavy getBacktestRun read is in
// flight). Cleared when the full result lands (or on load error). Not part of backtestStatus — it's a
// transient loading overlay, orthogonal to idle/running/done/error.
export const loadingRun = writable(null);

// 'idle' | 'running' | 'done' | 'error' — single source of truth for the view state machine.
export const backtestStatus = derived(
  [backtestRunning, backtestResult, backtestError],
  ([$running, $result, $error]) => {
    if ($running) return 'running';
    if ($error) return 'error';
    if ($result) return 'done';
    return 'idle';
  }
);

// Derived store for symbol details
export const selectedSymbolDetails = derived(
  [symbols, selectedSymbol],
  ([$symbols, $selectedSymbol]) => {
    return $symbols.find(s => s.symbol === $selectedSymbol) || null;
  }
);

// Derived store for portfolio value in USD
export const portfolioValue = derived(
  [positions, symbols],
  ([$positions, $symbols]) => {
    let totalValue = 0;

    // Guard against null or undefined positions
    if (!$positions || !Array.isArray($positions)) {
      return 0;
    }

    $positions.forEach(position => {
      // If the currency itself is USD or stablecoin, value is the balance
      if (position.baseCurrency === 'USD' || position.baseCurrency === 'USDT' || position.baseCurrency === 'USDC') {
        totalValue += position.totalBalance;
      } else {
        // Find the USD trading pair for this currency
        let usdPrice = 0;
        const base = position.baseCurrency?.toUpperCase();
        
        // Try direct USD pair first
        let symbolData = $symbols.find(s => s.symbol === `${base}/USD`);
        
        // If no USD pair, try USDT
        if (!symbolData) {
          symbolData = $symbols.find(s => s.symbol === `${base}/USDT`);
        }
        
        // If no USDT pair, try USDC
        if (!symbolData) {
          symbolData = $symbols.find(s => s.symbol === `${base}/USDC`);
        }
        
        if (symbolData) {
          usdPrice = symbolData.lastPrice || 0;
        } else if (position.baseCurrency !== 'BTC') {
          // Try to calculate through BTC
          const btcPair = $symbols.find(s => s.symbol === `${base}/BTC`);
          const btcUsdPair = $symbols.find(s => 
            s.symbol === 'BTC/USD' || s.symbol === 'BTC/USDT'
          );
          
          if (btcPair && btcUsdPair) {
            usdPrice = (btcPair.lastPrice || 0) * (btcUsdPair.lastPrice || 0);
          }
        }
        
        totalValue += position.totalBalance * usdPrice;
      }
    });
    
    return totalValue;
  }
);

// Helper function to format currency
export function formatCurrency(value, decimals = 2) {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value);
}

// Helper function to format crypto amount
export function formatCrypto(value, decimals = 8) {
  if (value === null || value === undefined) return '-';
  return parseFloat(value).toFixed(decimals).replace(/\.?0+$/, '');
}

// Helper function to format percentage
export function formatPercentage(value) {
  if (value === null || value === undefined) return '-';
  const formatted = parseFloat(value).toFixed(2);
  const prefix = value > 0 ? '+' : '';
  return `${prefix}${formatted}%`;
}